/* eslint-disable no-case-declarations */
import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import LocalStorageService from "@services/localStorage";
import ReportService from "@services/api/report";
import reportGraphqlInstance from "@services/api/report.graphql";
import VideoService from "@services/api/video";
import ChatRoomService from "@services/api/chat";
import { REPORT_TYPES, USER_ROLES } from "@utils/consts";
import { UserContext } from "./User";
import { io } from "socket.io-client";
import config from "@config/config";
import { sendNotify } from "@components/GlobalNotify/GlobalNotify";

export const AppointmentReportContext = createContext({
  appointmentReportItem: null,
  internalReportItem: null,
  createReport: async () => {},
  createAppointmentReportItem: () => {},
  createAppointmentReportCounsellorId: () => {},
  setCreatedReportId: () => {},
  createInternalReportItem: () => {},
  createInternalReport: async () => {},
  appointmentSocket: null,
  customReportConfigItem: null,
  createdReportId: null,
});

const AppointmentReportProvider = ({ children }) => {
  const { t } = useTranslation();
  const { profile: currentProfile } = useContext(UserContext);
  const [appointmentReportItem, setAppointmentReportItem] = useState();
  const [internalReportItem, setInternalReportItem] = useState();
  const [appointmentSocket, setAppointmentSocket] = useState();
  const [customReportConfigItem, setCustomReportConfigItem] = useState();
  const [createdReportId, setCreatedReportId] = useState(null);

  const socket = useRef();
  useEffect(() => {
    if (
      !currentProfile ||
      (currentProfile.type !== USER_ROLES.STUDENT_ROLE &&
        currentProfile.type !== USER_ROLES.COUNSELLOR_ROLE) ||
      (socket.current && socket.current.connected)
    ) {
      return undefined;
    }

    const accessToken = LocalStorageService.getAccessToken();

    if (!accessToken) {
      return undefined;
    }

    socket.current = io(config.calenderSocketDomain, {
      path: config.calenderSocketPath,
      query: {
        accessToken,
      },
    });

    setAppointmentSocket(socket.current);

    return () => {
      if (socket?.current && socket.current.connected) {
        socket.current.disconnect();
      }
    };
  }, [currentProfile?.id]);

  useEffect(() => {
    (async () => {
      const reportItem = LocalStorageService.getAppointmentReportItem();
      const counsellorId =
        LocalStorageService.getAppointmentReportCounsellorId();
      const internalReportItem = LocalStorageService.getInternalReportItem();
      if (
        !reportItem ||
        !currentProfile ||
        currentProfile.type !== USER_ROLES.COUNSELLOR_ROLE ||
        currentProfile.id !== counsellorId
      ) {
        return;
      }

      setAppointmentReportItem(reportItem);
      setInternalReportItem(internalReportItem);
    })();
  }, [currentProfile]);

  const createInternalReport = useCallback(
    async (data) => {
      if (!internalReportItem || !currentProfile) {
        return;
      }

      const report = {
        accountId: currentProfile.accountId,
        reporterId: currentProfile.userId,
        entityId: internalReportItem.reportItemId,
        type: internalReportItem.type,
        userId: null,
        ...data,
      };
      await ReportService.createReport(currentProfile.accountId, report).then(
        (createdReport) => {
          setCreatedReportId(createdReport.id);
        },
      );
      LocalStorageService.removeInternalReportItem();
      LocalStorageService.removeAppointmentReportCounsellorId();
      setInternalReportItem(null);
    },
    [internalReportItem, currentProfile],
  );

  const createReport = useCallback(
    async (createPayloadData) => {
      if (!appointmentReportItem || !currentProfile) {
        return;
      }

      const { shouldSyncToProrenata = false, ...reportPayloadOverrides } =
        createPayloadData || {};

      let entityId;
      let anotherParticipant;
      switch (
        reportPayloadOverrides?.data?.type
          ? reportPayloadOverrides?.data?.type
          : appointmentReportItem.type
      ) {
        case REPORT_TYPES.APPOINTMENT:
        case REPORT_TYPES.APPOINTMENT_QUICK:
          const room = await VideoService.getRoomById(
            appointmentReportItem?.accountId
              ? appointmentReportItem.accountId
              : currentProfile.accountId,
            appointmentReportItem.reportItemId,
            {
              includeParticipants: true,
            },
          );

          entityId = room.eventId;
          anotherParticipant = room.participants.find(
            (participant) =>
              participant.userId !==
              (appointmentReportItem?.currentUserId
                ? appointmentReportItem.currentUserId
                : currentProfile.userId),
          );
          break;
        case REPORT_TYPES.CHAT:
        case REPORT_TYPES.CHAT_QUICK:
          const chatRoom = await ChatRoomService.getChatRoomById(
            appointmentReportItem?.accountId
              ? appointmentReportItem.accountId
              : currentProfile.accountId,
            appointmentReportItem.reportItemId,
            true,
          );
          entityId = chatRoom.id;
          anotherParticipant = chatRoom.participants.find(
            (participant) =>
              participant.userId !==
              (appointmentReportItem?.currentUserId
                ? appointmentReportItem.currentUserId
                : currentProfile.userId),
          );
          break;
        case REPORT_TYPES.INSTANT:
        case REPORT_TYPES.QUICK:
          entityId = appointmentReportItem.reportItemId;
          anotherParticipant = {
            userId: appointmentReportItem.userId,
          };
          break;
        case REPORT_TYPES.ANONYMOUS_QUICK_REPORT:
        case REPORT_TYPES.ANONYMOUS_ULTRA_QUICK_REPORT:
          entityId = appointmentReportItem.reportItemId;
          anotherParticipant = {
            userId: null,
          };
          break;
        default:
          LocalStorageService.removeAppointmentReportItem();
          LocalStorageService.removeAppointmentReportCounsellorId();
          setAppointmentReportItem(null);
          return;
      }
      const report = {
        accountId: appointmentReportItem?.accountId
          ? appointmentReportItem.accountId
          : currentProfile.accountId,
        reporterId: appointmentReportItem?.currentUserId
          ? appointmentReportItem.currentUserId
          : currentProfile.userId,
        entityId,
        type: reportPayloadOverrides?.data?.type
          ? reportPayloadOverrides?.data?.type
          : appointmentReportItem.type,
        userId: anotherParticipant.userId,
        ...reportPayloadOverrides,
      };
      await ReportService.createReport(
        appointmentReportItem?.accountId
          ? appointmentReportItem.accountId
          : currentProfile.accountId,
        report,
      ).then((createdReport) => {
        setCreatedReportId(createdReport.id);
      });
      if (shouldSyncToProrenata) {
        try {
          const payloadForLeyr = {
            ...report,
            userId: Array.isArray(report.userId)
              ? report.userId
              : report.userId
                ? [report.userId]
                : [],
          };
          const syncResult =
            await reportGraphqlInstance.createReportForLeyr(payloadForLeyr);
          if (syncResult) {
            sendNotify(t("reports.prorenataToggle.success"), "success");
          } else {
            sendNotify(t("reports.prorenataToggle.error"), "error");
          }
        } catch (error) {
          console.error("Failed to sync report to Prorenata:", error);
          sendNotify(t("reports.prorenataToggle.error"), "error");
        }
      }
      LocalStorageService.removeAppointmentReportItem();
      LocalStorageService.removeAppointmentReportCounsellorId();
      setAppointmentReportItem(null);
    },
    [appointmentReportItem, currentProfile],
  );

  const createAppointmentReportItem = useCallback((reportItem) => {
    LocalStorageService.setAppointmentReportItem(reportItem);
    setAppointmentReportItem(reportItem);
  }, []);

  const createInternalReportItem = useCallback((internalReportItem) => {
    LocalStorageService.setInternalReportItem(internalReportItem);
    setInternalReportItem(internalReportItem);
  }, []);

  const createAppointmentReportCounsellorId = useCallback((id) => {
    LocalStorageService.setAppointmentReportCounsellorId(id);
  }, []);

  const value = {
    appointmentReportItem,
    createAppointmentReportItem,
    createInternalReportItem,
    createAppointmentReportCounsellorId,
    createReport,
    appointmentSocket,
    internalReportItem,
    createInternalReport,
    createdReportId,
    setCreatedReportId,
    customReportConfigItem,
    setCustomReportConfigItem,
    setAppointmentReportItem,
    setInternalReportItem,
  };

  return (
    <AppointmentReportContext.Provider value={{ ...value }}>
      {children}
    </AppointmentReportContext.Provider>
  );
};

export default AppointmentReportProvider;
